import { useMemo } from 'react'
import { Activity, ArrowRight, ClipboardList, Flag, MapPinned } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Kpi } from '@/components/ui/kpi'
import { Skeleton } from '@/components/ui/skeleton'
import { navPermissionCheck } from '@/components/layout/navVisibility'
import { useMe } from '@/features/auth/api'
import { Link } from '@/lib/router'
import { relativeTime } from '@/lib/format'

import { useTripReview, type TripReviewItem } from '@/features/trips/api'

/**
 * Dashboard widget — "Field trips" snapshot for the manager.
 *
 * Two compact KPIs at the top (active now / flagged needing review) +
 * up to five recent flagged trips with a click-through to the trip
 * detail page. Two link buttons — Live monitor / Trip review — give
 * the manager a one-click pivot to the dedicated pages when the
 * snapshot tips them off to something interesting.
 *
 * No backend additions; reuses /v1/trips/review with two filter sets.
 * Both queries inherit the 60s refetch from useTripReview.
 *
 * Visit review is a permission (TRIP_REVIEW_VIEW): without it the card is
 * not rendered at all, and its queries never run. Links follow their own
 * pages' permissions — Live visits (TRIP_LIVE_VIEW) and a single trip
 * (TRIP_DETAIL_VIEW).
 */
export function FieldTripsCard() {
  const { data: me } = useMe()
  const can = navPermissionCheck(me)
  if (!can('TRIP_REVIEW_VIEW')) return null
  return <FieldTripsCardBody canLive={can('TRIP_LIVE_VIEW')} canOpenTrip={can('TRIP_DETAIL_VIEW')} />
}

function FieldTripsCardBody({ canLive, canOpenTrip }: { canLive: boolean; canOpenTrip: boolean }) {
  const active = useTripReview({ status: 'ACTIVE', limit: 100 })
  const flagged = useTripReview({ flaggedOnly: true, limit: 5 })

  const flaggedItems = useMemo(() => flagged.data?.items ?? [], [flagged.data])
  const activeCount = active.data?.items.length ?? 0
  const flaggedCount = flaggedItems.length

  const loading = active.isLoading || flagged.isLoading

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MapPinned className="h-4 w-4" />
              Field visits
            </CardTitle>
            <CardDescription>
              Visits in progress, and recently flagged ones, for the agents you can review.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {canLive && (
              <Link to="/trips/live">
                <Button variant="outline" size="sm">
                  <Activity className="h-3.5 w-3.5" /> Live monitor
                </Button>
              </Link>
            )}
            <Link to="/trips/review">
              <Button variant="ghost" size="sm">
                <ClipboardList className="h-3.5 w-3.5" /> Review
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {(() => {
            const kpi = (
              <Kpi
                label="Active now"
                value={loading ? '—' : activeCount}
                hint={
                  loading
                    ? 'Loading'
                    : activeCount === 0
                      ? 'No one on the field right now'
                      : canLive
                        ? 'Click for the live map'
                        : 'Visits in progress'
                }
                loading={loading}
              />
            )
            return canLive ? (
              <Link to="/trips/live" className="block">
                {kpi}
              </Link>
            ) : (
              <div>{kpi}</div>
            )
          })()}
          <Link to="/trips/review" className="block">
            <Kpi
              label="Flagged visits"
              value={loading ? '—' : flaggedCount}
              hint={
                loading
                  ? 'Loading'
                  : flaggedCount === 0
                    ? 'No integrity flags raised'
                    : 'Click to open Visit review'
              }
              loading={loading}
              deltaSense="lower-is-better"
            />
          </Link>
        </div>

        <FlaggedList items={flaggedItems} loading={loading} canOpenTrip={canOpenTrip} />
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------

function FlaggedList({
  items,
  loading,
  canOpenTrip,
}: {
  items: TripReviewItem[]
  loading: boolean
  canOpenTrip: boolean
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }
  if (items.length === 0) {
    return (
      <p className="rounded-md border border-dashed bg-slate-50/60 px-3 py-4 text-center text-xs text-muted-foreground">
        Nothing flagged. Mock-location and time-gap reviews surface here.
      </p>
    )
  }
  return (
    <ul className="divide-y divide-slate-200 overflow-hidden rounded-md border">
      {items.map((item) => (
        <FlaggedRow key={item.tripId} row={item} canOpen={canOpenTrip} />
      ))}
    </ul>
  )
}

function FlaggedRow({ row, canOpen }: { row: TripReviewItem; canOpen: boolean }) {
  const body = (
    <>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge tone="warning">
              <Flag className="mr-1 h-3 w-3" /> {row.integrityFlagCount}
            </Badge>
            <span className="font-mono text-xs text-navy-900">
              {shortId(row.tripId)}
            </span>
            <span className="text-xs text-muted-foreground">
              agent {shortId(row.agentUserId)}
            </span>
          </div>
          {row.startedAt && (
            <div className="mt-0.5 text-2xs text-muted-foreground">
              started {relativeTime(row.startedAt)}
            </div>
          )}
        </div>
        {canOpen && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />}
    </>
  )
  const rowClass = 'flex items-center justify-between gap-3 bg-amber-50/40 px-3 py-2'
  return (
    <li>
      {canOpen ? (
        <Link
          to="/trips/$tripId"
          params={{ tripId: row.tripId }}
          className={`${rowClass} transition-colors hover:bg-amber-50/80`}
        >
          {body}
        </Link>
      ) : (
        <div className={rowClass}>{body}</div>
      )}
    </li>
  )
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id
}
